/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */
import React from 'react';
import PropTypes from "prop-types";
import { cloneElementWithCss } from '../../services/theme/clone_element';
import { euiTextAlignStyles } from './text_align.styles';
export var ALIGNMENTS = ['left', 'right', 'center'];
export var EuiTextAlign = ({
  children,
  textAlign = 'left',
  cloneElement = false,
  ...rest
}) => {
  const styles = euiTextAlignStyles();
  const cssStyles = [styles.euiTextAlign, styles[textAlign]];
  const props = {
    css: cssStyles,
    ...rest
  };

  if (cloneElement) {
    return cloneElementWithCss(children, props);
  } else {
    return <div {...props}>{children}</div>;
  }
};
EuiTextAlign.propTypes = {
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  textAlign: PropTypes.any,

  /**
       * Applies text styling to the child element instead of rendering a parent wrapper `div`.
       * Can only be used when wrapping a *single* child element/tag, and not raw text.
       */
  cloneElement: PropTypes.bool
};

try {
  EuiTextAlign.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/text/text_align.tsx',
    description: '',
    displayName: 'EuiTextAlign',
    methods: [],
    props: {
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: 'Defines a string value that labels the current element.\n' + '@see aria-labelledby.',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      textAlign: {
        defaultValue: {
          value: 'left'
        },
        description: '',
        name: 'textAlign',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/text/text_align.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"left" | "right" | "center"',
          value: [{
            value: '"left"'
          }, {
            value: '"right"'
          }, {
            value: '"center"'
          }]
        }
      },
      cloneElement: {
        defaultValue: {
          value: 'false'
        },
        description: 'Applies text styling to the child element instead of rendering a parent wrapper `div`.\n' + 'Can only be used when wrapping a *single* child element/tag, and not raw text.',
        name: 'cloneElement',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/text/text_align.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      }
    },
    extendedInterfaces: ['CommonProps', 'HTMLAttributes', 'AriaAttributes', 'DOMAttributes']
  };
} catch (e) {}